import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import * as Sentry from '@sentry/vue'
import { useAuthStore } from '@/stores/auth'
import { isTenantAdminType } from '@/types/users'
import { useToast } from '@/composables/useToast'
import KTUI from "@keenthemes/ktui/src/index.ts";
import KTComponent from "../metronic/core/index";
// @ts-ignore - Arquivo JS sem tipos
import KTLayout from '../metronic/app/layouts/demo1'
// Estender o tipo de meta das rotas para incluir permissões
declare module 'vue-router' {
  interface RouteMeta {
    title?: string
    requiresAuth?: boolean
    requiresTenantAdmin?: boolean
    strictAdminOnly?: boolean
  }
}

const routes: Array<RouteRecordRaw> = [
  // Redirecionar rota raiz para login
  {
    path: '/',
    redirect: '/auth/sign-in'
  },
  // Rotas de autenticação
  {
    path: '/auth',
    component: () => import('@/layouts/AuthLayout.vue'),
    children: [
      {
        path: 'sign-in',
        name: 'SignIn',
        component: () => import('@/views/auth/SignIn.vue'),
        meta: {
          title: 'Login',
          requiresAuth: false
        }
      },
      {
        path: 'forgot-password',
        name: 'ForgotPassword',
        component: () => import('@/views/auth/ForgotPassword.vue'),
        meta: {
          title: 'Recuperar Senha',
          requiresAuth: false
        }
      }
    ]
  },
  // Rotas da aplicação (protegidas)
  {
    path: '/app',
    component: () => import('@/layouts/AppLayout.vue'),
    meta: {
      requiresAuth: true
    },
    children: [
      {
        path: '',
        redirect: '/app/dashboard'
      },
      {
        path: 'dashboard',
        name: 'Dashboard',
        component: () => import('@/views/app/Dashboard.vue'),
        meta: {
          title: 'Dashboard'
        }
      },
      {
        path: 'tasks',
        name: 'Tasks',
        component: () => import('@/views/app/Tasks.vue'),
        meta: {
          title: 'Minhas Tarefas',
        }
      },
      {
        path: 'my-day',
        name: 'MyDay',
        component: () => import('@/views/app/MyDay.vue'),
        meta: {
          title: 'Meu Dia',
        }
      },
      {
        path: 'my-delays',
        name: 'MyDelays',
        component: () => import('@/views/app/MyDelays.vue'),
        meta: {
          title: 'Meus Atrasos',
        }
      },
      {
        path: 'plans',
        name: 'Plans',
        component: () => import('@/views/app/Planner.vue'),
        meta: {
          title: 'Planos',
        }
      },
      {
        path: 'plans/:id/settings',
        name: 'PlannerSettings',
        component: () => import('@/views/app/PlannerSettings.vue'),
        meta: {
          title: 'Configuração do Planner',
        },
      },
      {
        path: 'plans/:id/tasks',
        name: 'PlannerTasks',
        component: () => import('@/views/app/PlannerTasks.vue'),
        meta: {
          title: 'Tarefas do Planner',
        }
      },

      {
        path: 'profile',
        name: 'Profile',
        component: () => import('@/views/app/Profile.vue'),
        meta: {
          title: 'Meu Perfil'
        }
      },
      {
        path: 'account',
        name: 'Account',
        component: () => import('@/views/app/AccountSettings.vue'),
        meta: {
          title: 'Configurações da Conta'
        }
      },
      // Rotas de Configurações
      {
        path: 'settings/users',
        name: 'Users',
        component: () => import('@/views/app/settings/users/Users.vue'),
        meta: {
          title: 'Usuários',
        }
      },
      {
        path: 'settings/users/create',
        name: 'CreateUser',
        component: () => import('@/views/app/settings/users/CreateUser.vue'),
        meta: {
          title: 'Criar Usuário',
        }
      },
      {
        path: 'settings/users/edit/:id',
        name: 'EditUser',
        component: () => import('@/views/app/settings/users/EditUser.vue'),
        meta: {
          title: 'Editar Usuário',
        }
      },
      {
        path: 'settings/users/import',
        name: 'ImportUsers',
        component: () => import('@/views/app/settings/users/ImportUsers.vue'),
        meta: {
          title: 'Importar Usuários',
        }
      },
      {
        path: 'settings/groups',
        name: 'Groups',
        component: () => import('@/views/app/settings/groups/Groups.vue'),
        meta: {
          title: 'Grupos',
        }
      },
      {
        path: 'settings/groups/import',
        name: 'ImportGroups',
        component: () => import('@/views/app/settings/groups/ImportGroups.vue'),
        meta: {
          title: 'Importar Grupos',
        }
      },
      {
        path: 'settings/sectors',
        name: 'Sectors',
        component: () => import('@/views/app/settings/sectors/Sectors.vue'),
        meta: {
          title: 'Setores',
        }
      },
      {
        path: 'settings/sectors/import',
        name: 'ImportSectors',
        component: () => import('@/views/app/settings/sectors/ImportSectors.vue'),
        meta: {
          title: 'Importar Setores',
        }
      },
      {
        path: 'settings/access-levels',
        name: 'AccessLevels',
        component: () => import('@/views/app/settings/access-levels/AccessLevels.vue'),
        meta: {
          title: 'Níveis de Acesso',
        }
      },
      {
        path: 'settings/categories',
        name: 'Categories',
        component: () => import('@/views/app/settings/categories/Categories.vue'),
        meta: {
          title: 'Categorias',
        }
      },
      {
        path: 'settings/task-labels',
        name: 'TaskLabels',
        component: () => import('@/views/app/settings/task-labels/TaskLabels.vue'),
        meta: {
          title: 'Etiquetas',
        }
      },
      {
        path: 'settings/notifications',
        name: 'Notifications',
        component: () => import('@/views/app/settings/notifications/Notifications.vue'),
        meta: {
          title: 'Notificações',
        }
      },
      {
        path: 'settings/configurations',
        name: 'Configurations',
        component: () => import('@/views/app/settings/configurations/Configurations.vue'),
        meta: {
          title: 'Configurações do Sistema',
        }
      },
      {
        path: 'settings/business-rules',
        name: 'BusinessRules',
        component: () => import('@/views/app/settings/business-rules/BusinessRules.vue'),
        meta: {
          title: 'Regras de Negócio',
        }
      },
      {
        path: 'settings/tasks-list',
        name: 'TasksList',
        component: () => import('@/views/app/settings/tasks-list/TasksList.vue'),
        meta: {
          title: 'Configurações de Tarefas',
        }
      },
      {
        path: 'settings/plans/import',
        name: 'ImportPlans',
        component: () => import('@/views/app/settings/plans/ImportPlans.vue'),
        meta: {
          title: 'Planner',
        }
      },
      {
        path: 'settings/holidays/import',
        name: 'ImportHolidays',
        component: () => import('@/views/app/settings/holidays/ImportHolidays.vue'),
        meta: {
          title: 'Importar Feriados',
        }
      },
      {
        path: 'settings/audit-logs',
        name: 'AuditLogs',
        component: () => import('@/views/app/settings/audit-logs/AuditLogs.vue'),
        meta: {
          title: 'Auditoria',
          strictAdminOnly: true
        }
      }
    ]
  },
  // Rota para 404 - deve ser a última
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/views/NotFound.vue'),
    meta: {
      title: 'Página não encontrada'
    }
  }
]

// Criar instância do router
const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  // Configurações adicionais
  scrollBehavior(_, __, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else {
      return { top: 0 }
    }
  }
})

// Guards de navegação
router.beforeEach(async (to, _from, next) => {
  // Atualizar título da página
  if (to.meta.title) {
    document.title = `${to.meta.title} - FlowPilot`
  }

  // Verificar autenticação usando a store do Pinia
  const authStore = useAuthStore()
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth)

  // Se a rota requer autenticação, verificar se o usuário está autenticado
  if (requiresAuth) {
    // Verificar se já tem token E dados do usuário carregados
    if (!authStore.isAuthenticated) {
      const authRestored = await authStore.checkAuth()
      
      if (!authRestored) {
        next('/auth/sign-in')
        return
      }
    }
    
    if (to.path.startsWith('/app/settings') && !isTenantAdminType(authStore.user?.type)) {
      next('/app/dashboard')
      return
    }

    if (to.meta.requiresTenantAdmin && !isTenantAdminType(authStore.user?.type)) {
      next('/app/dashboard')
      return
    }

    if (to.meta.strictAdminOnly) {
      if (!isTenantAdminType(authStore.user?.type)) {
        setTimeout(() => {
          try {
            const toast = useToast()
            toast.error('Você não tem permissão para acessar a auditoria.')
          } catch (err) {
            console.warn('Não foi possível mostrar toast de erro:', err)
          }
        }, 100)

        next('/app/dashboard')
        return
      }
    }

    // KTComponent.init();
    // KTLayout.init();

    // Usuário autenticado e com permissões, permitir acesso
    next()
  } else if (to.path === '/auth/sign-in' && authStore.isAuthenticated) {
    // Se já está autenticado e tenta acessar login, redirecionar para dashboard
    next('/app/dashboard')
  } else {
    // Rota não protegida, permitir acesso
    next()
  }
})

// Erros de navegação (lazy load falhou, etc.)
router.onError((err) => {
  Sentry.captureException(err, { tags: { feature: 'router', type: 'navigation_error' } })
})

export default router